import { Loader2, MailX } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { ConsentBadge } from './ConsentBadge';
import { ListeDelContatto } from './liste/ListeDelContatto';
import { EventiDelContatto } from '../eventi/EventiDelContatto';
import type { ContactDetail } from '../../lib/email/emailService';
import {
  EMAIL_CONSENT_ACTION_LABEL,
  EMAIL_CONTACT_STATUS,
  type EmailLista,
} from '../../types/email.types';

const ETICHETTA_COLLEGAMENTO: Record<string, string> = {
  lead: 'Lead',
  customer: 'Cliente',
  supplier: 'Fornitore',
};

function dataIt(valore: string | null): string {
  if (!valore) return '—';
  return new Date(valore).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

interface SchedaContattoProps {
  dettaglio: ContactDetail | null;
  caricando: boolean;
  liste: EmailLista[];
  onChiudi: () => void;
  onDisiscrivi: () => void;
  onListeCambiate: () => void;
}

/** La scheda di un contatto: stato del consenso, liste, provenienza, storico. */
export function SchedaContatto({
  dettaglio,
  caricando,
  liste,
  onChiudi,
  onDisiscrivi,
  onListeCambiate,
}: SchedaContattoProps) {
  return (
    <Dialog open={Boolean(dettaglio) || caricando} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {caricando && !dettaglio ? (
          <div className="py-12 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        ) : dettaglio ? (
          <>
            <DialogHeader>
              <DialogTitle className="break-all">{dettaglio.contatto.email}</DialogTitle>
              <DialogDescription>
                {dettaglio.contatto.full_name || 'Nome non disponibile'}
                {dettaglio.contatto.company_name && ` · ${dettaglio.contatto.company_name}`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
                <ConsentBadge
                  status={dettaglio.contatto.status}
                  basis={dettaglio.contatto.consent_basis}
                  mostraBase
                />
                <span className="text-xs text-neutral-500">dal {dataIt(dettaglio.contatto.consent_at)}</span>
              </div>

              <p className="text-xs text-neutral-600 leading-relaxed">
                {EMAIL_CONTACT_STATUS[dettaglio.contatto.status].spiegazione}
              </p>

              <ListeDelContatto
                contattoId={dettaglio.contatto.id}
                liste={liste}
                onCambiato={onListeCambiate}
              />

              {dettaglio.collegamenti.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                    Dove compare in Pixel
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {dettaglio.collegamenti.map((l) => (
                      <span
                        key={l.id}
                        className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-neutral-100 text-neutral-700 border border-neutral-200"
                      >
                        {ETICHETTA_COLLEGAMENTO[l.entity_type] ?? l.entity_type}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <EventiDelContatto
                eventi={dettaglio.eventi ?? []}
                nonLeggibili={dettaglio.eventiNonLeggibili}
              />

              <div>
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                  Storico del consenso
                </p>
                {dettaglio.consensi.length === 0 ? (
                  <p className="text-xs text-neutral-400">
                    Nessun consenso registrato: non puo' ricevere campagne.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {dettaglio.consensi.map((k) => (
                      <li key={k.id} className="text-xs border-l-2 border-gray-200 pl-3 py-0.5">
                        <span className="font-medium text-gray-900">{EMAIL_CONSENT_ACTION_LABEL[k.action]}</span>
                        <span className="text-neutral-400"> · {dataIt(k.occurred_at)}</span>
                        {k.source && <div className="text-neutral-500 mt-0.5">{k.source}</div>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <DialogFooter className="gap-2">
              {!['disiscritto', 'reclamo', 'soppresso'].includes(dettaglio.contatto.status) && (
                <Button variant="outline" onClick={onDisiscrivi}>
                  <MailX className="w-4 h-4 mr-2" />
                  Disiscrivi
                </Button>
              )}
              <Button onClick={onChiudi}>Chiudi</Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
